/**
 * Bookstore Books — All Bookstore Books tab
 *
 * Shows books scraped from online bookstores. Admin marks each as
 * SCAN or DON'T SCAN and manually enters ISBNs.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, BookCheck, Ban, Undo2, Plus, ExternalLink, Loader2, Pencil, X, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { toast } from 'sonner';

// Table not yet in generated Supabase types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bookstoreTable = () => (supabase as any).from('bookstore_books');

interface BookstoreBook {
  id: string;
  title: string;
  author: string | null;
  publisher: string | null;
  description: string | null;
  source_url: string;
  source_store: string;
  cover_image_url: string | null;
  isbn: string | null;
  status: 'pending' | 'scan' | 'dont_scan';
  created_at: string;
  updated_at: string;
}

type StatusFilter = 'all' | 'pending' | 'scan' | 'dont_scan';

export function BookstoreBooks() {
  const [books, setBooks] = useState<BookstoreBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [editingIsbn, setEditingIsbn] = useState<string | null>(null);
  const [isbnValue, setIsbnValue] = useState('');
  const [scraping, setScraping] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [bookUrl, setBookUrl] = useState('');
  const [isbnInput, setIsbnInput] = useState('');
  const [editingBook, setEditingBook] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<BookstoreBook>>({});

  // ---------------------------------------------------------------------------
  // Load books
  // ---------------------------------------------------------------------------

  const loadBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Supabase default limit is 1000 rows. Fetch all with pagination.
      let allData: BookstoreBook[] = [];
      let page = 0;
      const PAGE_SIZE = 1000;
      while (true) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data: batch, error: fetchErr } = await bookstoreTable()
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, to);
        if (fetchErr) throw new Error(fetchErr.message);
        if (!batch || batch.length === 0) break;
        allData = allData.concat(batch);
        if (batch.length < PAGE_SIZE) break;
        page++;
      }
      setBooks(allData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load books');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleSetStatus = async (bookId: string, status: 'scan' | 'dont_scan' | 'pending') => {
    setActionInProgress(bookId);
    try {
      const { error: updateErr } = await bookstoreTable()
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to update status');
        return;
      }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, status } : b));
      toast.success(status === 'scan' ? 'Marked for scanning' : status === 'dont_scan' ? 'Marked as don\'t scan' : 'Reset to pending');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSaveIsbn = async (bookId: string) => {
    const trimmed = isbnValue.trim();
    if (!trimmed) return;

    setActionInProgress(bookId);
    try {
      const { error: updateErr } = await bookstoreTable()
        .update({ isbn: trimmed, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to save ISBN');
        return;
      }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, isbn: trimmed } : b));
      setEditingIsbn(null);
      setIsbnValue('');
      toast.success('ISBN saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save ISBN');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleStartEdit = (book: BookstoreBook) => {
    setEditingBook(book.id);
    setEditForm({
      title: book.title,
      author: book.author || '',
      publisher: book.publisher || '',
      description: book.description || '',
      isbn: book.isbn || '',
      cover_image_url: book.cover_image_url || '',
      source_url: book.source_url,
    });
  };

  const handleSaveEdit = async (bookId: string) => {
    setActionInProgress(bookId);
    try {
      const updates = {
        title: editForm.title || 'Untitled',
        author: editForm.author || null,
        publisher: editForm.publisher || null,
        description: editForm.description || null,
        isbn: editForm.isbn || null,
        cover_image_url: editForm.cover_image_url || null,
        source_url: editForm.source_url || '',
        updated_at: new Date().toISOString(),
      };

      const { error: updateErr } = await bookstoreTable()
        .update(updates)
        .eq('id', bookId);

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to save');
        return;
      }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updates } : b));
      setEditingBook(null);
      setEditForm({});
      toast.success('Book updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAddByIsbn = async () => {
    const isbn = isbnInput.trim().replace(/[-\s]/g, '');
    if (!isbn || isbn.length < 10) {
      toast.error('Please enter a valid ISBN (10 or 13 digits)');
      return;
    }

    setScraping(true);
    try {
      // Look up via Open Library API (free, no key needed)
      const res = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`);
      const data = await res.json();
      const key = `ISBN:${isbn}`;
      const book = data[key];

      let title = 'Unknown Title';
      let author: string | null = null;
      let publisher: string | null = null;
      let coverUrl: string | null = null;
      let description: string | null = null;

      if (book) {
        title = book.title || title;
        author = book.authors?.map((a: { name: string }) => a.name).join(', ') || null;
        publisher = book.publishers?.map((p: { name: string }) => p.name).join(', ') || null;
        coverUrl = book.cover?.large || book.cover?.medium || null;
        description = book.notes || book.subtitle || null;
      }

      // Insert into bookstore_books
      const { error: insertErr } = await bookstoreTable()
        .upsert({
          title,
          author,
          publisher,
          description,
          isbn,
          source_url: `https://openlibrary.org/isbn/${isbn}`,
          source_store: 'isbn_lookup',
          cover_image_url: coverUrl,
          status: 'pending',
        }, { onConflict: 'source_url', ignoreDuplicates: false });

      if (insertErr) {
        toast.error(insertErr.message || 'Failed to add book');
        return;
      }

      toast.success(`Added "${title}"${book ? '' : ' (ISBN not found in Open Library — added with ISBN only)'}`);
      setIsbnInput('');
      loadBooks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to look up ISBN');
    } finally {
      setScraping(false);
    }
  };

  const handleAddByUrl = async () => {
    const trimmedUrl = bookUrl.trim();
    if (!trimmedUrl || !trimmedUrl.startsWith('http')) {
      toast.error('Please enter a valid URL');
      return;
    }

    setScraping(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        toast.error('Not authenticated');
        return;
      }

      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-bookstore`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`,
            apikey: anonKey,
          },
          body: JSON.stringify({ url: trimmedUrl }),
        }
      );

      const json = await res.json();
      if (!res.ok || !json.success) {
        const detail = json.steps?.length ? ` (last step: ${json.steps[json.steps.length - 1]})` : '';
        toast.error(`${json.error || 'Scraping failed'}${detail}`);
        console.error('[scrape] Response:', json);
        return;
      }

      console.log('[scrape] Success:', json);
      toast.success(`Added "${json.book?.title || 'book'}"`);
      setBookUrl('');
      setShowAddUrl(false);
      loadBooks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Scraping failed');
    } finally {
      setScraping(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  const filtered = useMemo(() => {
    let list = books;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(b =>
        b.title.toLowerCase().includes(q) ||
        (b.author && b.author.toLowerCase().includes(q)) ||
        (b.isbn && b.isbn.includes(q))
      );
    }

    if (filterStatus !== 'all') {
      list = list.filter(b => b.status === filterStatus);
    }

    return list;
  }, [books, searchTerm, filterStatus]);

  const stats = useMemo(() => ({
    total: books.length,
    pending: books.filter(b => b.status === 'pending').length,
    scan: books.filter(b => b.status === 'scan').length,
    dontScan: books.filter(b => b.status === 'dont_scan').length,
    withIsbn: books.filter(b => b.status === 'scan' && b.isbn).length,
  }), [books]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
        <p className="text-[#FF3B30] text-lg font-semibold mb-2">Error Loading Books</p>
        <p className="text-[#8E8E93] text-sm mb-4">{error}</p>
        <button onClick={loadBooks} className="px-4 py-2 rounded-lg bg-[#111111] text-[#1A1A1A] text-sm font-medium">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1C1E]">All Bookstore Books</h2>
          <p className="text-sm text-[#8E8E93] mt-0.5">
            {stats.total} books scraped &middot; {stats.pending} pending &middot; {stats.scan} to scan &middot; {stats.withIsbn} ready for library
          </p>
        </div>
        <button
          onClick={() => setShowAddUrl(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#111111] text-white text-sm font-medium transition-colors hover:bg-[#333333] self-start"
        >
          <Plus className="w-4 h-4" />
          Add Book
        </button>
      </div>

      {/* Add book panel — by URL or ISBN */}
      {showAddUrl && (
        <div className="p-4 mb-6 border border-[#D4D4D4]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Add by URL */}
            <div>
              <p className="text-sm font-medium text-[#1A1A1A] mb-2">Add by URL</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={bookUrl}
                  onChange={e => setBookUrl(e.target.value)}
                  placeholder="https://www.booksaremagic.net/..."
                  className="flex-1 px-3 py-2.5 bg-white border border-[#D4D4D4] rounded-lg text-[#1C1C1E] text-sm placeholder-[#D4D4D4] focus:outline-none focus:border-[#1A1A1A]"
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleAddByUrl();
                    if (e.key === 'Escape') { setShowAddUrl(false); setBookUrl(''); }
                  }}
                />
                <button
                  onClick={handleAddByUrl}
                  disabled={scraping || !bookUrl.trim()}
                  className="px-4 py-2.5 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-[#333333] disabled:opacity-50"
                >
                  {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scrape'}
                </button>
              </div>
            </div>

            {/* Add by ISBN */}
            <div>
              <p className="text-sm font-medium text-[#1A1A1A] mb-2">Add by ISBN</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={isbnInput}
                  onChange={e => setIsbnInput(e.target.value.replace(/[^0-9X-]/gi, ''))}
                  placeholder="978-0-123456-78-9"
                  className="flex-1 px-3 py-2.5 bg-white border border-[#D4D4D4] rounded-lg text-[#1C1C1E] text-sm font-mono placeholder-[#D4D4D4] focus:outline-none focus:border-[#1A1A1A]"
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleAddByIsbn();
                    if (e.key === 'Escape') { setShowAddUrl(false); setIsbnInput(''); }
                  }}
                />
                <button
                  onClick={handleAddByIsbn}
                  disabled={scraping || !isbnInput.trim()}
                  className="px-4 py-2.5 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-[#333333] disabled:opacity-50"
                >
                  {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={() => { setShowAddUrl(false); setBookUrl(''); setIsbnInput(''); }}
            className="mt-3 text-xs text-[#888888] hover:text-[#1A1A1A]"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
          <input
            type="text"
            placeholder="Search title, author, ISBN..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A] focus:ring-1 focus:ring-[#1A1A1A]/20"
          />
        </div>

        <div className="flex gap-1">
          {([
            ['all', 'All'],
            ['pending', 'Pending'],
            ['scan', 'Scan'],
            ['dont_scan', "Don't Scan"],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setFilterStatus(value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                filterStatus === value
                  ? 'bg-[#111111] text-white'
                  : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.06]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <p className="text-[#8E8E93] text-sm">
            {books.length === 0
              ? 'No books scraped yet. Click "Scrape Now" to fetch books from Books Are Magic.'
              : 'No books match your current filters.'}
          </p>
        </div>
      )}

      {/* Book list */}
      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(book => {
            const isActioning = actionInProgress === book.id;
            const isEditingIsbn = editingIsbn === book.id;
            const isEditing = editingBook === book.id;

            if (isEditing) {
              const f = editForm;
              const field = (label: string, key: keyof BookstoreBook, multiline?: boolean) => (
                <div key={key}>
                  <label className="text-xs font-medium text-[#636366] block mb-1">{label}</label>
                  {multiline ? (
                    <textarea
                      value={(f[key] as string) || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-sm focus:outline-none focus:border-[#1A1A1A]"
                    />
                  ) : (
                    <input
                      type="text"
                      value={(f[key] as string) || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-sm focus:outline-none focus:border-[#1A1A1A]"
                    />
                  )}
                </div>
              );

              return (
                <div key={book.id} className="rounded-2xl p-4" style={glassStyles.card}>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#1C1C1E]">Edit Book</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(book.id)}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#111111] text-[#1A1A1A] text-xs font-medium disabled:opacity-30"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingBook(null); setEditForm({}); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#636366] text-xs"
                        >
                          <X className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {field('Title', 'title')}
                      {field('Author', 'author')}
                      {field('Publisher', 'publisher')}
                      {field('ISBN', 'isbn')}
                      {field('Cover Image URL', 'cover_image_url')}
                      {field('Source URL', 'source_url')}
                    </div>
                    {field('Description', 'description', true)}
                  </div>
                </div>
              );
            }

            return (
              <div key={book.id} className="rounded-2xl p-4" style={glassStyles.card}>
                <div className="flex gap-4">
                  {/* Cover image */}
                  {book.cover_image_url && (
                    <img
                      src={book.cover_image_url}
                      alt={book.title}
                      className="w-16 h-24 object-cover rounded-lg shrink-0 bg-black/[0.03]"
                    />
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-[#1C1C1E] truncate">{book.title}</h3>
                        {book.author && (
                          <p className="text-sm text-[#636366]">{book.author}</p>
                        )}
                        {book.publisher && (
                          <p className="text-xs text-[#8E8E93]">{book.publisher}</p>
                        )}
                      </div>

                      {/* Status badge */}
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        book.status === 'scan'
                          ? 'bg-[#34C759]/15 text-[#34C759]'
                          : book.status === 'dont_scan'
                            ? 'bg-black/[0.05] text-[#8E8E93]'
                            : 'bg-[#FF9F0A]/15 text-[#FF9F0A]'
                      }`}>
                        {book.status === 'scan' ? 'SCAN' : book.status === 'dont_scan' ? "DON'T SCAN" : 'PENDING'}
                      </span>
                    </div>

                    {/* Description */}
                    {book.description && (
                      <p className="text-xs text-[#8E8E93] mt-2 line-clamp-2">{book.description}</p>
                    )}

                    {/* ISBN row */}
                    <div className="flex items-center gap-2 mt-2">
                      {isEditingIsbn ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={isbnValue}
                            onChange={e => setIsbnValue(e.target.value)}
                            placeholder="Enter ISBN..."
                            className="px-3 py-1.5 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-xs font-mono focus:outline-none focus:border-[#1A1A1A] w-40"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveIsbn(book.id);
                              if (e.key === 'Escape') { setEditingIsbn(null); setIsbnValue(''); }
                            }}
                          />
                          <button
                            onClick={() => handleSaveIsbn(book.id)}
                            disabled={!isbnValue.trim() || isActioning}
                            className="px-2 py-1.5 rounded-lg bg-[#111111] text-[#1A1A1A] text-xs font-medium disabled:opacity-30"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingIsbn(null); setIsbnValue(''); }}
                            className="px-2 py-1.5 rounded-lg bg-black/[0.03] text-[#636366] text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          {book.isbn ? (
                            <span className="text-xs font-mono text-[#636366]">ISBN: {book.isbn}</span>
                          ) : (
                            <button
                              onClick={() => { setEditingIsbn(book.id); setIsbnValue(book.isbn || ''); }}
                              className="text-xs text-[#1A1A1A] hover:underline"
                            >
                              + Add ISBN
                            </button>
                          )}
                        </>
                      )}

                      <a
                        href={book.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-[#1A1A1A] hover:underline ml-auto"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View on site
                      </a>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-3">
                      {book.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleSetStatus(book.id, 'scan')}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#34C759]/15 text-[#34C759] text-xs font-medium hover:bg-[#34C759]/25 disabled:opacity-30"
                          >
                            <BookCheck className="w-3.5 h-3.5" />
                            Scan
                          </button>
                          <button
                            onClick={() => handleSetStatus(book.id, 'dont_scan')}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Don't Scan
                          </button>
                        </>
                      )}
                      {book.status === 'scan' && (
                        <button
                          onClick={() => handleSetStatus(book.id, 'pending')}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Undo
                        </button>
                      )}
                      {book.status === 'dont_scan' && (
                        <button
                          onClick={() => handleSetStatus(book.id, 'pending')}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Undo
                        </button>
                      )}
                      <button
                        onClick={() => handleStartEdit(book)}
                        disabled={isActioning}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#111111]/10 text-[#1A1A1A] text-xs font-medium hover:bg-[#111111]/20 disabled:opacity-30"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BookstoreBooks;
